import React, { useState, useEffect } from 'react';
import { Trash2, Check, Pencil, X } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Ligne d'un tableau "Lignes principales" d'une facture contrat.
 * - Lecture seule si editable=false.
 * - Si editable=true (facture en brouillon) : la désignation, la quantité et le P.U. HT
 *   sont éditables inline (toggle crayon → validation/annulation).
 */
export const InvoiceItemRow = ({ item, editable, autoEdit, onAutoEditConsumed, onUpdate, onRemove, formatPrice }) => {
  const [editing, setEditing] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [form, setForm] = useState({
    designation: item.designation || '',
    unite_payante: item.unite_payante || 'Forfait',
    quantite: item.quantite ?? 0,
    prix_unitaire_ht: item.prix_unitaire_ht ?? 0,
  });

  // Auto-open edit mode if requested (new line just added)
  useEffect(() => {
    if (autoEdit && editable) {
      setEditing(true);
      onAutoEditConsumed?.();
    }
  }, [autoEdit, editable, onAutoEditConsumed]);

  useEffect(() => {
    setForm({
      designation: item.designation || '',
      unite_payante: item.unite_payante || 'Forfait',
      quantite: item.quantite ?? 0,
      prix_unitaire_ht: item.prix_unitaire_ht ?? 0,
    });
  }, [item.id, item.designation, item.unite_payante, item.quantite, item.prix_unitaire_ht]);

  const isAdjustment = item.lineType === 'adjustment' || item.designation === 'Ajustement manuel';
  const rowClass = `border-t ${isAdjustment ? 'bg-amber-50/60' : ''}`;

  const submit = () => {
    const qty = parseFloat(String(form.quantite).replace(',', '.'));
    const pu = parseFloat(String(form.prix_unitaire_ht).replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(qty) || qty < 0 || !Number.isFinite(pu) || pu < 0) {
      return;
    }
    onUpdate({
      designation: (form.designation || '').trim() || item.designation,
      unite_payante: form.unite_payante || item.unite_payante || 'Forfait',
      quantite: qty,
      prix_unitaire_ht: pu,
    });
    setEditing(false);
  };

  const cancel = () => {
    setForm({
      designation: item.designation || '',
      unite_payante: item.unite_payante || 'Forfait',
      quantite: item.quantite ?? 0,
      prix_unitaire_ht: item.prix_unitaire_ht ?? 0,
    });
    setEditing(false);
  };

  if (!editable || !editing) {
    return (
      <tr className={rowClass} data-testid={`invoice-item-row-${item.id}`}>
        <td className="p-2">
          <p className="font-medium flex items-center gap-2">
            {item.designation}
            {isAdjustment && (
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded">ajustement</span>
            )}
          </p>
          {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
        </td>
        <td className="text-center p-2">{item.quantite} {item.unite_payante}</td>
        <td className="text-right p-2">{formatPrice(item.prix_unitaire_ht)}</td>
        <td className="text-right p-2 font-medium">{formatPrice(item.montant_total_ht)}</td>
        {editable && (
          <td className="p-2">
            <div className="flex items-center justify-end gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(true)}
                className="text-gray-500 hover:text-blue-600 h-6 w-6 p-0"
                title="Modifier la ligne"
                data-testid={`edit-invoice-item-${item.id}`}
              >
                <Pencil className="w-3 h-3" />
              </Button>
              {confirmingRemove ? (
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { onRemove(); setConfirmingRemove(false); }}
                    className="text-red-700 hover:bg-red-100 h-6 px-2 text-[11px] font-medium"
                    title="Confirmer la suppression"
                    data-testid={`confirm-remove-invoice-item-${item.id}`}
                  >
                    Supprimer ?
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmingRemove(false)}
                    className="text-gray-500 hover:text-gray-700 h-6 w-6 p-0"
                    title="Annuler"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmingRemove(true)}
                  className="text-red-600 hover:text-red-800 h-6 w-6 p-0"
                  title="Supprimer la ligne"
                  data-testid={`remove-invoice-item-${item.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </td>
        )}
      </tr>
    );
  }

  // Edit mode
  return (
    <tr className="border-t bg-blue-50/40" data-testid={`invoice-item-edit-${item.id}`}>
      <td className="p-2">
        <input
          type="text"
          value={form.designation}
          onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
          className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:border-blue-500"
          placeholder="Désignation"
          data-testid={`edit-designation-${item.id}`}
        />
        {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
      </td>
      <td className="p-2">
        <div className="flex items-center gap-1">
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.quantite}
            onChange={(e) => setForm((f) => ({ ...f, quantite: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') cancel(); }}
            className="w-16 px-2 py-1 text-sm text-center border border-blue-300 rounded focus:outline-none focus:border-blue-500"
            data-testid={`edit-quantite-${item.id}`}
          />
          <select
            value={form.unite_payante}
            onChange={(e) => setForm((f) => ({ ...f, unite_payante: e.target.value }))}
            className="flex-1 px-1 py-1 text-xs border border-blue-300 rounded bg-white focus:outline-none focus:border-blue-500"
            data-testid={`edit-unite-${item.id}`}
          >
            <option value="Forfait">Forfait</option>
            <option value="Camion/mois">Camion/mois</option>
            <option value="Jour">Jour</option>
            <option value="Tonne">Tonne</option>
            <option value="Heure">Heure</option>
            <option value="Course">Course</option>
            <option value="Transfert">Transfert</option>
            <option value="Opération">Opération</option>
          </select>
        </div>
      </td>
      <td className="p-2">
        <input
          type="number"
          step="1"
          min="0"
          value={form.prix_unitaire_ht}
          onChange={(e) => setForm((f) => ({ ...f, prix_unitaire_ht: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') cancel(); }}
          className="w-full px-2 py-1 text-sm text-right border border-blue-300 rounded focus:outline-none focus:border-blue-500"
          data-testid={`edit-pu-${item.id}`}
        />
      </td>
      <td className="text-right p-2 text-gray-400 italic">—</td>
      <td className="p-2">
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={submit}
            className="text-green-600 hover:text-green-800 h-6 w-6 p-0"
            title="Valider (Entrée)"
            data-testid={`save-invoice-item-${item.id}`}
          >
            <Check className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={cancel}
            className="text-gray-500 hover:text-gray-700 h-6 w-6 p-0"
            title="Annuler (Échap)"
            data-testid={`cancel-invoice-item-${item.id}`}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default InvoiceItemRow;
